import { createLogger } from "utils/logging";
import { fetchRetry } from "utils/fetch";
import { DetailviewService } from "./DetailviewService";
import { TrackingService } from "./tracking/TrackingService";
import { toggle } from "@otto-ec/global-resources/toggle";
import { captureErrorWithPayload } from "utils/error";
import { createExpiryScheduler, type ExpiryScheduler } from "utils/expiry";

const attributeNameVariationId = "variation-id";
const attributeNameProductId = "product-id";
const attributeNameEventMergeId = "event-merge-id";
const attributeNameOriginFeature = "feature";
const attributeNameTeam = "team";
const attributeNameIsPrefetched = "is-prefetched";

const searchParamBenefitId = "benefitId";
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const log = createLogger("tag", "detailview.component");
const DETAILVIEW_ELEMENT_NAME = "ft9-detailview-v1";

const ERROR_KEY = "ft9-benefit-detailview-not-ok";

export class DetailviewElement extends HTMLElement {
	private readonly detailviewService: DetailviewService;
	expiry: ExpiryScheduler = createExpiryScheduler();
	private autoActivateBenefitId: string | undefined;

	constructor() {
		super(); /*                                     */
		this.detailviewService = new DetailviewService(new TrackingService());
	}

	getAutoactiveBenefitId() {
		/*                                              */
		return this.autoActivateBenefitId;
	}

	async connectedCallback() {
		if (this.getAttribute(attributeNameIsPrefetched) === "true") {
			log.info("Detailview is prefetched, skipping backend call.");
			this.initDetailview();
			return;
		}
		await this.callBackend();
	}

	disconnectedCallback() {
		this.expiry.cleanup();
	}

	private async callBackend() {
		this.replaceChildren(); /*                       */

		const variationId = this.getAttribute(attributeNameVariationId);
		if (!variationId) {
			log.error("No variationId provided, cannot load detailview.");
			captureErrorWithPayload("No variationId provided", "tag", "detailview", ERROR_KEY);
			return;
		}

		const benefitId = new URLSearchParams(window.location.search).get(searchParamBenefitId);
		if (benefitId && UUID_REGEX.test(benefitId)) {
			this.autoActivateBenefitId = benefitId;
		}

		const urlSearchParams = this.getSearchParams();
		log.info("Calling tag for %s with %s", this.id || "id-not-set", urlSearchParams.toString());

		try {
			const response = await fetchRetry({
				url: `/benefit-tag/detailview?${urlSearchParams}`,
				namespace: "detailview",
				fetchOptions: {
					headers: {
						Accept: "text/html;charset=UTF-8",
						"X-Requested-With": "XMLHttpRequest",
					},
					priority: "high",
				},
				retryOptions: {
					limit: 2,
					delay: 50,
				},
			});

			if (!response.ok) {
				log.error("detailview endpoint returns a non ok response!", response.status);

				const labels = {
					url_search_params: urlSearchParams.toString(),
					tries: 1,
				};

				captureErrorWithPayload("callBackend error", "tag", "detailview", ERROR_KEY, response.status, labels);
				return;
			}

			if (response.status === 200) {
				response.text().then((text) => {
					this.innerHTML = text;
					this.initDetailview();
				});
				return;
			}

			this.detailviewService.handleNoContentDetailview(variationId, this);
		} catch (error) {
			log.error("detailview request returned an error!", error);

			const labels = {
				url_search_params: urlSearchParams.toString(),
				tries: 3,
			};
			captureErrorWithPayload("callBackend error", "tag", "detailview", ERROR_KEY, error, labels);
		}
	}

	private getSearchParams() {
		const urlSearchParams = new URLSearchParams();

		function addToSearchParam(paramName: string, value: string | null) {
			if (value != null) {
				urlSearchParams.append(paramName, value);
			}
		}

		addToSearchParam("variationId", this.getAttribute(attributeNameVariationId));
		addToSearchParam("productId", this.getAttribute(attributeNameProductId));
		addToSearchParam("team", this.getAttribute(attributeNameTeam));
		addToSearchParam("originFeature", this.getAttribute(attributeNameOriginFeature));
		if (this.autoActivateBenefitId) {
			addToSearchParam("benefitId", this.autoActivateBenefitId);
		}
		return urlSearchParams;
	}

	private initDetailview() {
		const variationId = this.getAttribute(attributeNameVariationId);
		const eventMergeId = this.getAttribute(attributeNameEventMergeId);

		if (!variationId) {
			return;
		}

		this.detailviewService.initialize(variationId, this, eventMergeId);
	}

	async triggerReload() {
		await this.callBackend();
	}
}

if (customElements && customElements.get(DETAILVIEW_ELEMENT_NAME) === undefined && !toggle.get("ftnine_emergency_custom_element_disable_detailview", false)) {
	customElements.define(DETAILVIEW_ELEMENT_NAME, DetailviewElement);
}
